/** Accès portail envoyés (mot de passe temporaire) que le client n'a pas encore utilisés. */
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Send } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function PendingResetsPanel() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/resets", { params: { status: "envoye" } });
      setItems(r.data?.items || []);
    } catch { /* silencieux : panneau optionnel */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/resets/${r.id}/resend`);
      toast.success(`Courriel d'accès renvoyé à ${r.client_name || r.client_email}`);
      window.dispatchEvent(new CustomEvent("badges:refresh"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setBusyId(null);
    }
  };

  if (!items.length) return null;

  return (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="pending-resets-panel">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="w-4 h-4 text-amber-700" />
        <p className="text-sm font-semibold text-amber-900">
          Accès envoyés, jamais utilisés
          <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" data-testid="pending-resets-count">{items.length}</span>
        </p>
        <span className="text-[11px] text-amber-700 ml-auto">La pastille disparaît quand le client se connecte (ou après 48 h).</span>
      </div>
      <ul className="divide-y divide-amber-100">
        {items.map((r) => (
          <li key={r.id} className="py-1.5 flex items-center gap-3 text-xs" data-testid={`pending-reset-${r.id}`}>
            <span className="font-semibold text-slate-800 truncate">{r.client_name || "—"}</span>
            <span className="text-slate-500 truncate">{r.client_email}</span>
            <span className="text-slate-400 ml-auto whitespace-nowrap">envoyé {fmt(r.created_at)} · expire {fmt(r.expires_at)}</span>
            <button
              onClick={() => resend(r)}
              disabled={busyId === r.id}
              className="px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`pending-reset-resend-${r.id}`}
            >
              {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Renvoyer
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
